import React, { useState } from 'react';
import {
	EMPTY_PIPELINE_JOB_STATS,
	formatAvgPresentationTime,
	getInProcessDonutStyle
} from '../pipelineJobStats';

const cx = (...classes) => classes.filter(Boolean).join(' ');

const OverviewIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true">
		<rect x="1.5" y="8" width="3" height="6.5" rx="0.75" fill="currentColor" />
		<rect x="6.5" y="5" width="3" height="9.5" rx="0.75" fill="currentColor" />
		<rect
			x="11.5"
			y="2.5"
			width="3"
			height="12"
			rx="0.75"
			fill="currentColor"
		/>
	</svg>
);

// Tiles are clickable wrappers that themselves contain clickable sub-filters,
// so they are rendered as role="button" divs to avoid nesting <button>s.
const StatTile = ({ active, className, onToggle, ariaLabel, children }) => (
	<div
		className={cx('pipeline-job-stats-tile', active && 'is-active', className)}
		role="button"
		tabIndex={0}
		aria-pressed={active}
		aria-label={ariaLabel}
		onClick={onToggle}
		onKeyDown={event => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				onToggle();
			}
		}}>
		{children}
	</div>
);

const SubFilterButton = ({ active, className, onToggle, children }) => (
	<button
		type="button"
		className={cx(className, active && 'is-active')}
		aria-pressed={active}
		onClick={event => {
			event.stopPropagation();
			onToggle();
		}}>
		{children}
	</button>
);

const PipelineJobStats = ({ stats, activeFilter, onFilterChange, loading }) => {
	const [expanded, setExpanded] = useState(false);
	const isLoading = !stats && loading;
	const isReady = Boolean(stats) && !loading;
	const { inProcess, rejected, withdrawn, onHold, hired, avgPresentationDays } =
		stats || EMPTY_PIPELINE_JOB_STATS;
	const avgPresentationTime = formatAvgPresentationTime(avgPresentationDays);

	const isActive = filterId => activeFilter === filterId;
	const toggleFilter = filterId =>
		onFilterChange(isActive(filterId) ? null : filterId);

	return (
		<div
			className={cx(
				'pipeline-job-stats-section',
				expanded && 'pipeline-job-stats-section--expanded'
			)}>
			<button
				type="button"
				className="pipeline-job-stats-header"
				aria-expanded={expanded}
				onClick={() => setExpanded(current => !current)}>
				<span className="pipeline-job-stats-header-leading">
					<span className="pipeline-job-stats-header-icon">
						<OverviewIcon />
					</span>
					<span className="pipeline-job-stats-header-label">Overview</span>
				</span>
				<img
					className="pipeline-job-stats-header-chevron"
					src="/ats/icons/chevron-down-icon.svg"
					alt=""
				/>
			</button>
			<div
				className={cx(
					'pipeline-job-stats-body',
					expanded && 'pipeline-job-stats-body--expanded'
				)}
				aria-hidden={!expanded}>
				<div className="pipeline-job-stats-body-inner">
					{isLoading ? (
						<div
							className="pipeline-job-stats pipeline-job-stats--loading"
							aria-busy="true"
						/>
					) : (
						<div
							className={cx(
								'pipeline-job-stats',
								isReady && 'pipeline-job-stats--visible'
							)}
							role="group"
							aria-label="Job position KPIs">
							<StatTile
								active={isActive('in_process')}
								className="pipeline-job-stats-tile--in-process"
								ariaLabel="In Process candidates"
								onToggle={() => toggleFilter('in_process')}>
								<div className="pipeline-job-stats-in-process">
									<div className="pipeline-job-stats-in-process-legend">
										<span className="pipeline-job-stats-tile-title">
											In Process
										</span>
										<SubFilterButton
											active={isActive('internal_review')}
											className="pipeline-job-stats-legend-row"
											onToggle={() => toggleFilter('internal_review')}>
											<span className="pipeline-job-stats-legend-swatch pipeline-job-stats-legend-swatch--light" />
											<span className="pipeline-job-stats-legend-label">
												{inProcess.internalReview} Internal Review
											</span>
										</SubFilterButton>
										<SubFilterButton
											active={isActive('with_client')}
											className="pipeline-job-stats-legend-row"
											onToggle={() => toggleFilter('with_client')}>
											<span className="pipeline-job-stats-legend-swatch pipeline-job-stats-legend-swatch--dark" />
											<span className="pipeline-job-stats-legend-label">
												{inProcess.withClient} With Client
											</span>
										</SubFilterButton>
									</div>
									<div
										className="pipeline-job-stats-donut"
										style={getInProcessDonutStyle(
											inProcess.internalReview,
											inProcess.withClient
										)}
										aria-hidden="true">
										<span className="pipeline-job-stats-donut-value">
											{inProcess.total}
										</span>
									</div>
								</div>
							</StatTile>

							<StatTile
								active={isActive('rejected')}
								className="pipeline-job-stats-tile--breakdown"
								ariaLabel="Rejected candidates"
								onToggle={() => toggleFilter('rejected')}>
								<div className="pipeline-job-stats-tile-header">
									<span className="pipeline-job-stats-tile-title">
										Rejected
									</span>
									<span className="pipeline-job-stats-tile-total pipeline-job-stats-tile-total--error">
										{rejected.total}
									</span>
								</div>
								<div className="pipeline-job-stats-breakdown">
									<SubFilterButton
										active={isActive('rejected_by_talent')}
										className="pipeline-job-stats-breakdown-row"
										onToggle={() => toggleFilter('rejected_by_talent')}>
										<span className="pipeline-job-stats-breakdown-label">
											By Talent Team
										</span>
										<span className="pipeline-job-stats-breakdown-value pipeline-job-stats-breakdown-value--error">
											{rejected.byTalent}
										</span>
									</SubFilterButton>
									<SubFilterButton
										active={isActive('rejected_by_client')}
										className="pipeline-job-stats-breakdown-row"
										onToggle={() => toggleFilter('rejected_by_client')}>
										<span className="pipeline-job-stats-breakdown-label">
											By Client
										</span>
										<span className="pipeline-job-stats-breakdown-value pipeline-job-stats-breakdown-value--error">
											{rejected.byClient}
										</span>
									</SubFilterButton>
								</div>
							</StatTile>

							<StatTile
								active={isActive('withdrawn')}
								className="pipeline-job-stats-tile--breakdown"
								ariaLabel="Withdrawn candidates"
								onToggle={() => toggleFilter('withdrawn')}>
								<div className="pipeline-job-stats-tile-header">
									<span className="pipeline-job-stats-tile-title">
										Withdrawn
									</span>
									<span className="pipeline-job-stats-tile-total pipeline-job-stats-tile-total--muted">
										{withdrawn.total}
									</span>
								</div>
								<div className="pipeline-job-stats-breakdown">
									<SubFilterButton
										active={isActive('unresponsive')}
										className="pipeline-job-stats-breakdown-row"
										onToggle={() => toggleFilter('unresponsive')}>
										<span className="pipeline-job-stats-breakdown-label">
											Unresponsive
										</span>
										<span className="pipeline-job-stats-breakdown-value pipeline-job-stats-breakdown-value--muted">
											{withdrawn.unresponsive}
										</span>
									</SubFilterButton>
									<SubFilterButton
										active={isActive('not_interested')}
										className="pipeline-job-stats-breakdown-row"
										onToggle={() => toggleFilter('not_interested')}>
										<span className="pipeline-job-stats-breakdown-label">
											Not Interested
										</span>
										<span className="pipeline-job-stats-breakdown-value pipeline-job-stats-breakdown-value--muted">
											{withdrawn.notInterested}
										</span>
									</SubFilterButton>
								</div>
							</StatTile>

							{onHold > 0 && (
								<StatTile
									active={isActive('on_hold')}
									className="pipeline-job-stats-tile--stat pipeline-job-stats-tile--on-hold"
									ariaLabel="On Hold candidates"
									onToggle={() => toggleFilter('on_hold')}>
									<span className="pipeline-job-stats-tile-title">On Hold</span>
									<span className="pipeline-job-stats-stat-box pipeline-job-stats-stat-box--neutral">
										{onHold}
									</span>
								</StatTile>
							)}

							<StatTile
								active={isActive('hired')}
								className="pipeline-job-stats-tile--stat pipeline-job-stats-tile--hired"
								ariaLabel="Hired candidates"
								onToggle={() => toggleFilter('hired')}>
								<span className="pipeline-job-stats-tile-title">Hired</span>
								<span className="pipeline-job-stats-stat-box pipeline-job-stats-stat-box--success">
									{hired}
								</span>
							</StatTile>

							<div className="pipeline-job-stats-tile pipeline-job-stats-tile--avg">
								<span className="pipeline-job-stats-tile-title">
									Avg. Time in Presentation
								</span>
								<span
									className="pipeline-job-stats-avg-box"
									title={avgPresentationTime.label}>
									{avgPresentationTime.parts.map((part, index) => (
										<span className="pipeline-job-stats-avg-part" key={index}>
											<span className="pipeline-job-stats-avg-value">
												{part.value}
											</span>
											<span className="pipeline-job-stats-avg-unit">
												{part.unit}
											</span>
										</span>
									))}
								</span>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default PipelineJobStats;
